import React, { useState, useEffect } from 'react';
import { Modal, Button, Steps, Input, Typography, Space, Checkbox, Alert, Spin } from 'antd';
import {
    QrcodeOutlined,
    SafetyOutlined,
    CheckCircleOutlined,
    CopyOutlined,
    DownloadOutlined,
    PrinterOutlined,
    MobileOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
    setupMFA,
    verifySetup,
    closeSetupModal,
    selectShowSetupModal,
    selectSetupData,
    selectMFALoading,
    selectMFAError,
    selectIsSettingUp
} from '../../redux/slices/mfaSlice';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const MFASetupModal = () => {
    const dispatch = useDispatch();

    // Redux state
    const visible = useSelector(selectShowSetupModal);
    const setupData = useSelector(selectSetupData);
    const loading = useSelector(selectMFALoading);
    const isSettingUp = useSelector(selectIsSettingUp);
    const error = useSelector(selectMFAError);

    // Local state
    const [currentStep, setCurrentStep] = useState(0);
    const [totpCode, setTotpCode] = useState('');
    const [backupCodesSaved, setBackupCodesSaved] = useState(false);

    // Debug: Track step changes
    useEffect(() => {
        console.log('Current step changed to:', currentStep);
        console.log('Setup data:', setupData);
    }, [currentStep, setupData]);

    // Initialize setup when modal opens
    useEffect(() => {
        if (visible && currentStep === 0 && !setupData.qrCodeUrl) {
            // Wait for user to click "Get Started" before calling setup API
        }
    }, [visible]);

    // Auto-advance to step 1 when QR code data is available
    useEffect(() => {
        if (visible && setupData.qrCodeUrl && currentStep < 1) {
            setCurrentStep(1);
        }
    }, [visible, setupData.qrCodeUrl]);

    // Reset state when modal closes
    useEffect(() => {
        console.log('Modal visibility changed:', visible);
        if (!visible) {
            console.log('Modal closed, resetting state');
            setCurrentStep(0);
            setTotpCode('');
            setBackupCodesSaved(false);
        }
    }, [visible]);

    const handleClose = () => {
        dispatch(closeSetupModal());
    };

    const handleGetStarted = async () => {
        try {
            await dispatch(setupMFA()).unwrap();
            setCurrentStep(1);
        } catch (_) {
            // Error toasts handled in thunk
        }
    };

    const handleContinueToVerify = () => {
        setCurrentStep(2);
    };

    const handleVerifyCode = async () => {
        if (!totpCode || totpCode.length !== 6) {
            toast.error('Please enter a valid 6-digit code', {
                position: 'top-right',
                duration: 3000,
            });
            return;
        }

        if (!setupData.setupSessionId) {
            toast.error('Setup session not found. Please restart the setup process.', {
                position: 'top-right',
                duration: 3000,
            });
            return;
        }

        console.log('Verifying MFA setup code...');
        const result = await dispatch(verifySetup({ 
            totpCode, 
            setupSessionId: setupData.setupSessionId 
        }));
        console.log('Verify result:', result);
        if (result.type.endsWith('/fulfilled')) {
            console.log('Verification successful, advancing to step 3');
            setCurrentStep(3);
        } else {
            console.log('Verification failed');
        }
    };

    const handleCopySecret = () => {
        if (setupData.secret) {
            navigator.clipboard.writeText(setupData.secret);
            toast.success('Secret code copied to clipboard', {
                position: 'top-right',
                duration: 2000,
            });
        }
    };

    const handleCopyBackupCodes = () => {
        if (setupData.backupCodes && setupData.backupCodes.length > 0) {
            const codesText = setupData.backupCodes.join('\n');
            navigator.clipboard.writeText(codesText);
            toast.success('Backup codes copied to clipboard', {
                position: 'top-right',
                duration: 2000,
            });
        }
    };

    const handleDownloadBackupCodes = () => {
        if (setupData.backupCodes && setupData.backupCodes.length > 0) {
            const codesText = `Neurula Health - MFA Backup Codes\n\nGenerated: ${new Date().toLocaleString('en-GB')}\n\n${setupData.backupCodes.join('\n')}\n\n⚠️ IMPORTANT: Keep these codes secure!\n• Each code can only be used once\n• Use these if you lose access to your authenticator app\n• Store them in a secure location`;

            const blob = new Blob([codesText], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `neurula-mfa-backup-codes-${Date.now()}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast.success('Backup codes downloaded', {
                position: 'top-right',
                duration: 2000,
            });
        }
    };

    const handlePrintBackupCodes = () => {
        if (setupData.backupCodes && setupData.backupCodes.length > 0) {
            const printWindow = window.open('', '_blank');
            printWindow.document.write(`
                <html>
                <head>
                    <title>Neurula Health - MFA Backup Codes</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 40px; }
                        h1 { color: #2E5A87; }
                        .codes { margin: 20px 0; font-family: monospace; font-size: 14px; }
                        .code { margin: 8px 0; padding: 8px; background: #f5f5f5; }
                        .warning { color: #E53935; margin-top: 20px; }
                        .footer { margin-top: 40px; color: #666; font-size: 12px; }
                    </style>
                </head>
                <body>
                    <h1>Neurula Health - MFA Backup Codes</h1>
                    <p>Generated: ${new Date().toLocaleString('en-GB')}</p>
                    <div class="codes">
                        ${setupData.backupCodes.map((code, index) => `<div class="code">${index + 1}. ${code}</div>`).join('')}
                    </div>
                    <div class="warning">
                        <strong>⚠️ IMPORTANT:</strong>
                        <ul>
                            <li>Keep these codes secure!</li>
                            <li>Each code can only be used once</li>
                            <li>Use these if you lose access to your authenticator app</li>
                            <li>Store them in a secure location</li>
                        </ul>
                    </div>
                    <div class="footer">
                        <p>Neurula Health EMR - Confidential</p>
                    </div>
                </body>
                </html>
            `);
            printWindow.document.close();
            printWindow.print();
        }
    };

    const handleFinish = () => {
        if (!backupCodesSaved) {
            toast.error('Please confirm that you have saved your backup codes', {
                position: 'top-right',
                duration: 3000,
            });
            return;
        }
        dispatch(closeSetupModal());
    };

    // Step 0: Introduction
    const renderIntroduction = () => (
        <div style={{ padding: '20px 0' }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <SafetyOutlined style={{ fontSize: '64px', color: '#2E5A87' }} />
            </div>

            <Title level={4} style={{ textAlign: 'center', marginBottom: '24px' }}>
                What You'll Need
            </Title>

            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                    <Paragraph>
                        <MobileOutlined style={{ fontSize: '20px', marginRight: '8px', color: '#2E5A87' }} />
                        <strong>A smartphone or tablet</strong>
                    </Paragraph>
                </div>

                <div>
                    <Paragraph>
                        <strong>An authenticator app</strong> (we recommend):
                    </Paragraph>
                    <ul style={{ marginLeft: '28px' }}>
                        <li>Google Authenticator</li>
                        <li>Microsoft Authenticator</li>
                        <li>Authy</li>
                        <li>1Password</li>
                    </ul>
                </div>

                <Alert
                    message="This will take about 2 minutes"
                    description="Your account will be more secure after completing this setup."
                    type="info"
                    showIcon
                />
            </Space>
        </div>
    );

    // Step 1: Scan QR Code
    const renderQRCode = () => (
        <div style={{ padding: '20px 0' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                    <Paragraph>
                        <strong>1.</strong> Open your authenticator app
                    </Paragraph>
                    <Paragraph>
                        <strong>2.</strong> Tap "Add Account" or "+" button
                    </Paragraph>
                    <Paragraph>
                        <strong>3.</strong> Scan this QR code:
                    </Paragraph>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" tip="Generating QR code..." />
                    </div>
                ) : setupData.qrCodeUrl ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <img
                            src={setupData.qrCodeUrl}
                            alt="MFA QR Code"
                            style={{
                                width: '200px',
                                height: '200px',
                                border: '2px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '10px',
                                background: '#fff'
                            }}
                        />
                    </div>
                ) : null}

                <Alert
                    message="Can't scan?"
                    description={
                        <div>
                            <Text>Enter this code manually in your authenticator app:</Text>
                            <div style={{
                                marginTop: '8px',
                                padding: '12px',
                                background: '#f5f5f5',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                wordBreak: 'break-all',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span>{setupData.secret}</span>
                                <Button
                                    type="text"
                                    icon={<CopyOutlined />}
                                    onClick={handleCopySecret}
                                    size="small"
                                />
                            </div>
                        </div>
                    }
                    type="info"
                />
            </Space>
        </div>
    );

    // Step 2: Verify Code
    const renderVerifyCode = () => (
        <div style={{ padding: '20px 0' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Paragraph>
                    Enter the 6-digit code from your authenticator app to confirm setup:
                </Paragraph>

                <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                        Verification Code:
                    </Text>
                    <Input
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        size="large"
                        style={{
                            fontSize: '24px',
                            letterSpacing: '8px',
                            textAlign: 'center',
                            fontFamily: 'monospace'
                        }}
                        autoFocus
                    />
                </div>

                {error && (
                    <Alert
                        message="Verification Failed"
                        description={error}
                        type="error"
                        showIcon
                    />
                )}

                <Alert
                    message="ℹ️ Code refreshes every 30 seconds"
                    description="Open your authenticator app and enter the code for 'Neurula Health'"
                    type="info"
                />
            </Space>
        </div>
    );

    // Step 3: Save Backup Codes
    const renderBackupCodes = () => (
        <div style={{ padding: '20px 0' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Alert
                    message="⚠️ IMPORTANT: Save these backup codes"
                    description="Use these codes if you lose access to your authenticator app. Each code can only be used once."
                    type="warning"
                    showIcon
                />

                <div style={{
                    padding: '16px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    maxHeight: '240px',
                    overflowY: 'auto'
                }}>
                    {setupData.backupCodes && setupData.backupCodes.map((code, index) => (
                        <div
                            key={index}
                            style={{
                                padding: '8px',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                borderBottom: index < setupData.backupCodes.length - 1 ? '1px solid #e0e0e0' : 'none'
                            }}
                        >
                            {index + 1}. {code}
                        </div>
                    ))}
                </div>

                <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadBackupCodes}
                    >
                        Download
                    </Button>
                    <Button
                        icon={<CopyOutlined />}
                        onClick={handleCopyBackupCodes}
                    >
                        Copy All
                    </Button>
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={handlePrintBackupCodes}
                    >
                        Print
                    </Button>
                </Space>

                <Checkbox
                    checked={backupCodesSaved}
                    onChange={(e) => setBackupCodesSaved(e.target.checked)}
                >
                    <strong>I have saved my backup codes</strong>
                </Checkbox>
            </Space>
        </div>
    );

    const steps = [
        {
            title: 'Introduction',
            icon: <SafetyOutlined />,
        },
        {
            title: 'Scan QR Code',
            icon: <QrcodeOutlined />,
        },
        {
            title: 'Verify Code',
            icon: <CheckCircleOutlined />,
        },
        {
            title: 'Backup Codes',
            icon: <CopyOutlined />,
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    <SafetyOutlined style={{ color: '#2E5A87' }} />
                    <span>Set Up Two-Factor Authentication</span>
                </Space>
            }
            open={visible}
            onCancel={handleClose}
            footer={null}
            width={700}
            centered
            destroyOnClose
            maskClosable={false}
        >
            <div style={{ marginTop: '24px' }}>
                <Steps current={currentStep} items={steps} style={{ marginBottom: '32px' }} />

                <div style={{ minHeight: '400px' }}>
                    {currentStep === 0 && renderIntroduction()}
                    {currentStep === 1 && renderQRCode()}
                    {currentStep === 2 && renderVerifyCode()}
                    {currentStep === 3 && renderBackupCodes()}
                </div>

                <div style={{ marginTop: '24px', textAlign: 'right' }}>
                    <Space>
                        {currentStep > 0 && currentStep < 3 && (
                            <Button onClick={() => setCurrentStep(currentStep - 1)}>
                                Back
                            </Button>
                        )}
                        <Button onClick={handleClose}>
                            Cancel
                        </Button>
                        {currentStep === 0 && (
                            <Button type="primary" onClick={handleGetStarted} loading={isSettingUp}>
                                Get Started
                            </Button>
                        )}
                        {currentStep === 1 && (
                            <Button type="primary" onClick={handleContinueToVerify}>
                                Continue
                            </Button>
                        )}
                        {currentStep === 2 && (
                            <Button
                                type="primary"
                                onClick={handleVerifyCode}
                                loading={loading}
                                disabled={totpCode.length !== 6}
                            >
                                Verify
                            </Button>
                        )}
                        {currentStep === 3 && (
                            <Button
                                type="primary"
                                onClick={handleFinish}
                                disabled={!backupCodesSaved}
                                icon={<CheckCircleOutlined />}
                            >
                                Finish
                            </Button>
                        )}
                    </Space>
                </div>
            </div>
        </Modal>
    );
};

export default MFASetupModal;
